import { useCallback } from 'react';

import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { EmployeeProfilePages } from '@/modules/user/views/UserProfile/types';
import routes from '@/pages/corporate/routes';

interface ProfileRouteOptions {
  /** Use the admin mirror of the academy profile when the productivity model is off. */
  isAdmin?: boolean;
  /**
   * Academy profile tab. Only applies to the academy page — the workeffy SPA names its tabs
   * differently, so a deep link there would have to be mapped rather than passed through.
   */
  tab?: EmployeeProfilePages;
}

/**
 * Resolves where a user's profile lives. Workspaces on the productivity model read their
 * profiles from the workeffy SPA at `/w/profile/:id`; everyone else stays on the academy
 * page. Crossing into `/w` needs a full page load, which `Link` and `useNavigation` handle.
 */
const useProfileRoute = () => {
  const { [FeatureFlags.EnableProductivityModel]: isProductivityModelEnabled } = useFeatureFlags();

  return useCallback(
    (userId: string, { isAdmin = false, tab }: ProfileRouteOptions = {}) => {
      if (isProductivityModelEnabled) {
        return routes.UserMgsaProfile(userId);
      }

      return isAdmin ? routes.AdminUserProfile(userId, tab) : routes.UserProfile(userId, tab);
    },
    [isProductivityModelEnabled],
  );
};

export default useProfileRoute;
